import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Divider, Grid, Paper, TextField, Typography, Alert, Stack, Tooltip,
} from '@mui/material';
import {
  ArrowBack, Business, AccountBalance, Payments, ReceiptLong, CheckCircle, Article,
  Timeline as TimelineIcon, PlayCircle, DoneAll,
} from '@mui/icons-material';
import { procurementService, VendorPayment } from '../../services/procurementService';
import { StatusDot } from '../../components/common/pageKit';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';

const inr = (n: any) => `₹${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)}`;
const fmtDate = (d: any) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const num = (v: any) => Number(v) || 0;

const STATUS_TONE: Record<string, any> = { PENDING: 'warning', PROCESSED: 'info', COMPLETED: 'success', CANCELLED: 'neutral' };

const SectionCard: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
    <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      {icon}
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Paper>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid size={{ xs: 6, sm: 4 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Grid>
);

const VendorPaymentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pay, setPay] = useState<VendorPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [bankRef, setBankRef] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { payment } = await procurementService.getVendorPayment(id);
      setPay(payment);
      setBankRef(payment.bank_reference || '');
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load payment');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const doProcess = async () => {
    if (!id) return;
    setBusy(true);
    try { await procurementService.processVendorPayment(id); await load(); }
    catch (e: any) { setError(e.message || 'Process failed'); }
    finally { setBusy(false); }
  };
  const doComplete = async () => {
    if (!id) return;
    setBusy(true);
    try { await procurementService.completeVendorPayment(id, bankRef.trim() || undefined); setCompleteOpen(false); await load(); }
    catch (e: any) { setError(e.message || 'Complete failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  if (error && !pay) return <Box sx={{ p: 3 }}><Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert></Box>;
  if (!pay) return null;

  const isPending = pay.status === 'PENDING';
  const isProcessed = pay.status === 'PROCESSED';
  const isCompleted = pay.status === 'COMPLETED';
  const isCancelled = pay.status === 'CANCELLED';
  const posted = isProcessed || isCompleted; // posts to GL on process

  return (
    <Box sx={{ p: 3 }}>
      <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/procurement/payments')} sx={{ mb: 1.5 }}>Vendor Payments</Button>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header band */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 20 }}>{pay.payment_number}</Typography>
            <Typography variant="body2" color="text.secondary">{pay.vendor_name}{pay.invoice_no ? ` · against ${pay.invoice_no}` : ''}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">Net Paid</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{inr(pay.net_amount)}</Typography>
          </Box>
          <Stack spacing={0.75} alignItems="flex-end">
            <StatusDot label={String(pay.status || '').replace(/_/g, ' ')} tone={STATUS_TONE[pay.status] || 'neutral'} />
            <Typography variant="caption" color="text.secondary">{fmtDate(pay.payment_date)}</Typography>
          </Stack>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {isPending && <Button variant="contained" size="small" startIcon={<PlayCircle />} disabled={busy} onClick={doProcess}>Process Payment</Button>}
          {isProcessed && (
            <Tooltip title="Mark as paid with the bank/UTR reference">
              <span><Button variant="contained" size="small" startIcon={<DoneAll />} disabled={busy} onClick={() => setCompleteOpen(true)}>Mark Completed</Button></span>
            </Tooltip>
          )}
          {pay.invoice_id && <Button variant="text" size="small" startIcon={<ReceiptLong />} onClick={() => navigate(`/procurement/invoices/${pay.invoice_id}`)}>View Invoice {pay.invoice_no || ''}</Button>}
          {pay.vendor_id && <Button variant="text" size="small" startIcon={<Business />} onClick={() => navigate(`/entity/supplier/${pay.vendor_id}`)}>View Vendor</Button>}
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          {/* Summary */}
          <SectionCard title="Summary" icon={<Business fontSize="small" color="primary" />}>
            <Grid container spacing={2}>
              <KV label="Vendor" value={`${pay.vendor_name || '—'}${pay.vendor_code ? ` (${pay.vendor_code})` : ''}`} />
              <KV label="Invoice" value={pay.invoice_no || '—'} />
              <KV label="Payment Date" value={fmtDate(pay.payment_date)} />
              <KV label="Payment Mode" value={String(pay.payment_mode || '—').replace(/_/g, ' ')} />
              <KV label="Bank Reference" value={pay.bank_reference || '—'} />
              <KV label="Vendor PAN" value={pay.vendor_pan || '—'} />
            </Grid>
            {pay.notes && <><Divider sx={{ my: 2 }} /><Typography variant="caption" color="text.secondary" display="block">Notes</Typography><Typography variant="body2">{pay.notes}</Typography></>}
          </SectionCard>

          {/* Amounts */}
          <SectionCard title="Amounts & TDS" icon={<Payments fontSize="small" color="primary" />}>
            <Grid container spacing={2}>
              <KV label="Gross Amount" value={inr(pay.gross_amount)} />
              <KV label="TDS Section" value={pay.tds_section || '—'} />
              <KV label="TDS Rate" value={pay.tds_rate != null ? `${pay.tds_rate}%` : '—'} />
              <KV label="TDS Amount" value={inr(pay.tds_amount)} />
              <KV label="Net Amount" value={<Box component="span" sx={{ fontWeight: 800 }}>{inr(pay.net_amount)}</Box>} />
            </Grid>
            {num(pay.tds_amount) > 0 && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                TDS of {inr(pay.tds_amount)} withheld under section {pay.tds_section || '—'} — deposited to the TDS payable ledger, net {inr(pay.net_amount)} paid to the vendor.
              </Alert>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          {/* Bank details */}
          <SectionCard title="Vendor Bank" icon={<AccountBalance fontSize="small" color="primary" />}>
            <Grid container spacing={1.5}>
              <KV label="Bank" value={pay.vendor_bank_name || '—'} />
              <KV label="Account" value={pay.vendor_bank_account || '—'} />
              <KV label="IFSC" value={pay.vendor_ifsc_code || '—'} />
            </Grid>
          </SectionCard>

          {/* Accounting */}
          <SectionCard title="Accounting" icon={<AccountBalance fontSize="small" color="primary" />}>
            {posted ? (
              <Alert severity="success" icon={<CheckCircle fontSize="inherit" />}>
                Posted to the ledger — Dr Accounts Payable, Cr Bank{num(pay.tds_amount) > 0 ? ' + TDS Payable' : ''}.
                <Button size="small" sx={{ ml: 1 }} onClick={() => navigate('/finance/journal')}>View Journal</Button>
              </Alert>
            ) : (
              <Alert severity="info">Not yet posted. The payment posts to the general ledger when it is processed.</Alert>
            )}
          </SectionCard>

          <Box sx={{ mb: 2 }}>
            <AttachmentsPanel entityType="vendor_payment" entityId={pay.id} title="Payment Documents" />
          </Box>

          {/* Lifecycle */}
          <SectionCard title="Lifecycle" icon={<TimelineIcon fontSize="small" color="primary" />}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center"><CheckCircle fontSize="small" color="success" /><Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Created</Typography><Typography variant="caption" color="text.secondary">{fmtDate(pay.created_at)}</Typography></Box></Stack>
              <Stack direction="row" spacing={1} alignItems="center">{posted ? <CheckCircle fontSize="small" color="success" /> : <Article fontSize="small" color="disabled" />}<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Processed</Typography><Typography variant="caption" color="text.secondary">{posted ? 'Posted to GL' : 'Pending'}</Typography></Box></Stack>
              <Stack direction="row" spacing={1} alignItems="center">{isCompleted ? <CheckCircle fontSize="small" color="success" /> : (isCancelled ? <Article fontSize="small" color="error" /> : <Payments fontSize="small" color="disabled" />)}<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>{isCancelled ? 'Cancelled' : 'Completed'}</Typography><Typography variant="caption" color="text.secondary">{isCompleted ? `Paid · ${pay.bank_reference || 'no ref'}` : (isCancelled ? 'Payment cancelled' : 'Awaiting bank confirmation')}</Typography></Box></Stack>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Complete dialog */}
      <Dialog open={completeOpen} onClose={() => setCompleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark {pay.payment_number} completed</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Confirm the bank has released this payment. The bank/UTR reference is recorded against the payment.
          </DialogContentText>
          <TextField fullWidth label="Bank / UTR reference" value={bankRef} onChange={(e) => setBankRef(e.target.value)} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={doComplete}>Mark Completed</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorPaymentDetailPage;
